// Slide numbers for a deck (TODO §19.0.3, then §18.2.9).
//
// The number used to be `index + 1`, computed at each of the four places that draw it. The
// cover page ended that: a cover carries a date and a client, **not** a page number, and it
// must not consume one either — a deck whose cover counted as page 1 numbers every slide
// after it one too high, in the editor, in presentation, in the PDF and in the exported
// HTML, all four disagreeing with the printed deck in someone's hand.
//
// §18.2.9 then made the number **hierarchical**, and with that it stopped being cosmetic. A
// storyline slide is `4`; its evidence slides are `4.1`, `4.2`; backups are `B1..Bn`. The
// number therefore *states the slide's place in the argument*, which is the whole point of
// the pyramid — a slide photographed off a screen or pulled out of a printout says what it is
// supporting and where it sits. Backups get their own `B` series because they belong to no
// storyline slide and a decimal would imply otherwise.
//
// Two consequences worth carrying:
//
//   • The on-canvas number is inside `.slide-canvas`, so it lands in the PDF, the exported
//     HTML and presentation. **This is export-path code**, not chrome.
//   • A total-count suffix (`4.2 / 27`) no longer parses. Nothing shows one today; if one is
//     ever wanted it has to be against its own series, never against the deck.
//
// Every renderer looks its slide up here rather than deriving a number from an array index.
// Nothing in the app computes a slide number from a position again.

import { DEPTH_BACKUP, DEPTH_EVIDENCE, depthOf } from "@/lib/pyramid";
import type { SlideData } from "@/types/document";

/**
 * Hierarchical numbers for `slides` **in the order given**, which must be the ACTIVE slides:
 * archived ones are not part of the deck and are excluded by every caller before this.
 *
 * A cover yields `null` — "draw no number here" — and does not advance any counter, so the
 * first storyline slide after a cover is `1`.
 */
export function slideNumbering(slides: SlideData[]): (string | null)[] {
  let storyline = 0;
  let evidence = 0;
  let backup = 0;
  return slides.map((slide) => {
    if (slide.layout === "cover") return null;
    const d = depthOf(slide);
    if (d === DEPTH_BACKUP) return `B${++backup}`;
    // `storyline === 0` cannot happen once the invariants hold (§18.1.3 (a) makes the first
    // active slide a storyline slide), but an evidence slide with no parent would otherwise
    // number as `0.1`; falling through to the storyline series keeps the output readable for
    // any input, which matters because this runs over an imported deck before anything else.
    if (d === DEPTH_EVIDENCE && storyline > 0)
      return `${storyline}.${++evidence}`;
    evidence = 0;
    return String(++storyline);
  });
}
